import React, {useState, useEffect} from 'react';
import {Box, Grid, Typography, CircularProgress} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import '@fontsource/roboto';
import GlobalReportOptionsForm from './options/GlobalReportOptionsForm';
import DownloadButtonWithHaders from '../../../components/reports/DownloadButtonWithHaders';
import GlobalReportDataGrid from './components/GlobalReportDataGrid';
import {fetchGlobalReport} from '../../../API/GlobalReport';
import OutletIcon from '@mui/icons-material/Outlet';
import {toast} from 'react-toastify';
import {format} from 'date-fns';

const CSV_DATE_COLUMN = {id: 'date', title: 'Date'};

const CSV_COLUMNS = [
    {id: 'irtb_id', title: 'iRTB ID'},
    {id: 'account_name', title: 'Account Name'},
    {id: 'account_manager', title: 'Account Manager'},
    {id: 'type', title: 'Type'},
    {id: 'platform', title: 'Platform'},
    {id: 'requests', title: 'Requests'},
    {id: 'avg_daily_qps', title: 'Avg Daily QPS'},
    {id: 'responses', title: 'Responses'},
    {id: 'impressions', title: 'Impressions'},
    {id: 'clicks', title: 'Clicks'},
    {id: 'ssp_win_rate', title: 'SSP Win Rate %'},
    {id: 'dsp_win_rate', title: 'DSP Win Rate %'},
    {id: 'fill_rate', title: 'Fill Rate %'},
    {id: 'rcpm', title: 'rCPM'},
    {id: 'dsp_ecpm', title: 'DSP eCPM'},
    {id: 'total_internal_revenue', title: 'Total Internal Revenue $'},
    {id: 'smarthub_revenue', title: 'SmartHub Revenue $'},
    {id: 'rixengine_revenue', title: 'RixEngine (RE) Revenue $'},
    // {id: 'aniview_revenue', title: 'Aniview (AV) Revenue $'}, // Aniview integration is not connected yet
    {id: 'cost', title: 'Cost $'},
    {id: 'gross_profit', title: 'Gross Profit $'},
    {id: 'margin', title: 'Margin %'},
    {id: 'avg_daily_rev_7d', title: 'AVG Daily Rev for Last 7 Days'},
    {id: 'avg_daily_rev_14d', title: 'AVG Daily Rev for Last 14 Days'},
    {id: 'avg_monthly_profit', title: 'AVG Monthly Profit (Last 3 Months)'},
    {id: 'api_rev', title: 'API Rev $'},
    {id: 'api_gap', title: 'API Gap $'},
    {id: 'api_gap_pct', title: 'API Gap %'},
];

const GlobalReport = () => {
    const [data, setData] = useState([]);
    const [dataForCsv, setDataForCsv] = useState([]);
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedFields, setSelectedFields] = useState([]);
    const [groupBy, setGroupBy] = useState('total');

    useEffect(() => {
        if (data && data.length > 0) {
            const preparedData = data.map((row) => row.value);
            const fieldColumns = selectedFields && selectedFields.length > 0
                ? CSV_COLUMNS.filter((col) => selectedFields.includes(col.id))
                : CSV_COLUMNS;
            const visibleCsvColumns = groupBy === 'day' ? [CSV_DATE_COLUMN, ...fieldColumns] : fieldColumns;
            setCsvHeaders(visibleCsvColumns.map((col) => col.title));
            const processedData = preparedData.map((item) => {
                const newItem = {};
                visibleCsvColumns.forEach((header) => {
                    newItem[header.title] = item[header.id] !== undefined && item[header.id] !== null ? item[header.id] : '';
                });
                return newItem;
            });
            setDataForCsv(processedData);
        } else {
            setCsvHeaders([]);
            setDataForCsv([]);
        }

        setName(`GlobalReport - ${format(new Date(), 'yyyy-MM-dd')}`);
    }, [data, selectedFields, groupBy]);

    const handleSubmit = async (options) => {
        try {
            setSelectedFields(options.fields || []);
            setLoading(true);
            const response = await fetchGlobalReport(options);
            setGroupBy(options.group_by === 'day' ? 'day' : 'total');
            if (!response?.data || Object.keys(response.data).length === 0) {
                setData(false);
            } else {
                setData(Object.entries(response.data).map(([key, value]) => ({key, value})));
            }
            setLoading(false);
        } catch (err) {
            toast.error('Oops, there seems to be an error on the server. The technical team has already received information about this. We apologize for the inconvenience and we will let you know when it is fixed.');
            console.error(err);
            setLoading(false);
        }
    };

    return (
        <div>
            <Grid container spacing={2}
                  sx={{
                      padding: 2,
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '4px',
                      paddingLeft: 0,
                      paddingTop: 0,
                      marginLeft: '-10px',
                      marginTop: '-5px',
                      boxShadow: '0 0 10px rgba(0, 0, 0, 0.08)',
                  }}
            >
                <Grid item xs={12} md={12} sx={{width: '100%'}}>
                    <Accordion sx={{width: '100%'}}>
                        <AccordionSummary
                            expandIcon={<InfoOutlinedIcon/>}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                            sx={{
                                backgroundColor: '#f0f3f5',
                                width: '100%',
                                height: '35px !important',
                                minHeight: '25px !important',
                            }}
                        >
                            Explanations
                        </AccordionSummary>
                        <AccordionDetails sx={{width: '100%'}}>
                            <Box>
                                <Typography variant="subtitle1" sx={{marginBottom: '1rem'}}>
                                    Unified high-level view of SmartHub and RixEngine accounts: totals, margins, and API gaps.
                                </Typography>
                                <Typography variant="body1" sx={{marginLeft: '1rem', marginBottom: '0.5rem'}}>
                                    Use Date Range, Partner Name, Account Manager, Platform and Type (DSP / SSP) filters, then Generate Report.
                                </Typography>
                                <Typography variant="body1" sx={{marginLeft: '1rem', marginBottom: '0.5rem'}}>
                                    Group by Total shows one row per account for the whole period. Group by Day shows one row per account per day; the Total row is the same in both modes. AVG Daily Rev and AVG Monthly Profit describe the account, so they repeat on every day row and are counted once in Total.
                                </Typography>
                                <Typography variant="body1" sx={{marginLeft: '1rem'}}>
                                    Expand Total Internal Revenue with “+” to see SmartHub and RixEngine breakdown. Color flags: rCPM &lt; 0.1 red / &gt; 1 green; AVG Monthly Profit &lt; $500 red / &gt; $1000 green; API Gap % outside ±5% red.
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            </Grid>

            <Grid container spacing={2}
                  sx={{
                      padding: 2,
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '4px',
                      paddingLeft: 0,
                      marginLeft: '-10px',
                      marginTop: '20px',
                      boxShadow: '0 0 10px rgba(0, 0, 0, 0.08)',
                  }}
            >
                <Grid item xs={12} md={12} sx={{width: '100%'}}>
                    <GlobalReportOptionsForm onSubmit={handleSubmit}/>
                </Grid>
                {loading &&
                    <Grid item xs={12} md={12}>
                        <Box justifyContent="center" textAlign="center" alignItems="center">
                            <CircularProgress size={200}/>
                        </Box>
                    </Grid>}
                <Grid item xs={12} md={12}>
                    {data && data !== false && data.length !== 0 &&
                        <DownloadButtonWithHaders
                            data={dataForCsv}
                            headers={csvHeaders}
                            label={name}
                        />}
                </Grid>
                <Grid item xs={12} md={12}>
                    {data === false ? (
                        <Typography variant="h4" component="h2" align="center">
                            <OutletIcon fontSize="large"/> NO DATA
                        </Typography>
                    ) : (
                        <GlobalReportDataGrid data={data} selectedFields={selectedFields} groupBy={groupBy}/>
                    )}
                </Grid>
            </Grid>
        </div>
    );
};

export default GlobalReport;
